// Treasure Collect – "From five layers to one treasure": the display assembles itself while the page scrolls.
// Real-time WebGL (tc-asm3d.js, the Blender scene with its own materials) when the browser supports WebGL 2;
// otherwise the rendered frame sequence, decoded ahead and painted on a canvas.
(() => {
  const MOTION_END = 0.8;           // share of the timeline where the layers move; the rest is the light sweep
  const STEPS = [0.1, 0.32, 0.6];   // when each caption of the frame fallback lights up
  // the five named layers: mesh, the point the column of captions is hung from (01 and 05 set its top and bottom),
  // and the layer's right edge [x, top y, bottom y, z], all in the mesh's own coordinates. Every leader meets its layer's
  // edge at the caption's own height, so the five lines stay perfectly horizontal.
  const LAYERS = {
    front: ['vetro davanti', [40, 44, 0], [40, 57, -57, 0]],
    card: ['carta', [0.5, 0.26, 0], [0.5, 0.44, -0.44, 0]],
    frame: ['vetro in mezzo', [40, 4, -6], [40, 57, -57, -6]],
    art: ['immagine', [1, -0.3, 0], [1, 0.9, -0.9, 0]],
    back: ['vetro dietro', [40, -40, -10], [40, 57, -57, -10]],
  };
  const SIDE_GAP = 30;    // desktop: space between the render's framing and the caption column
  const EDGE = 24;        // captions never come closer than this to the edge of the stage
  const SAFE_BOTTOM = 96; // nor to the bottom of the screen, where the chat button sits
  const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
  const smooth = (a, b, x) => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };
  const webgl2 = () => {
    try { return !!(window.WebGL2RenderingContext && document.createElement('canvas').getContext('webgl2')); } catch (e) { return false; }
  };

  const init = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const total = parseInt(root.dataset.frames, 10) || 1;
    const base = root.dataset.base;
    const query = root.dataset.query || '';
    const img = root.querySelector('.tc-asm__img');
    const steps = [...root.querySelectorAll('.tc-asm__steps li')];
    const cta = root.querySelector('.tc-asm__cta');
    const frameBox = root.querySelector('.tc-asm__frame');
    const glCanvas = root.querySelector('.tc-asm__gl');
    const labelsBox = root.querySelector('.tc-asm__labels');
    const labels = labelsBox ? [...labelsBox.querySelectorAll('.tc-asm__label')] : [];
    const lines = labelsBox ? [...labelsBox.querySelectorAll('.tc-asm__line')] : [];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    let shown = 0;      // eased progress on screen
    let target = 0;     // progress from the scroll position
    let gl = null;      // the WebGL scene once it is ready
    let use3d = !!(root.dataset.three && glCanvas && webgl2());
    let visible = false;
    let idle = 0;       // 0..1: how much of the "alive" motion of the finished display is on

    // ---------- frame sequence (fallback) ----------
    let size = 'l';
    const url = (i) => `${base}tc-asm-${size}-${String(i + 1).padStart(2, '0')}.webp${query}`;
    let loaded = false;
    let current = -1;
    const cv = root.querySelector('.tc-asm__cv');
    const ctx = cv && cv.getContext ? cv.getContext('2d', { alpha: true }) : null;
    const frames = [];
    const preload = () => {
      if (loaded || use3d) return;
      loaded = true;
      const hd = root.querySelector('.tc-asm__hd[data-src]');
      if (hd) { hd.src = hd.dataset.src; hd.removeAttribute('data-src'); }
      size = window.innerWidth < 750 && (window.devicePixelRatio || 1) < 2 ? 's' : 'l';
      const order = [];
      for (const stride of [8, 4, 2, 1]) {
        for (let i = 0; i < total; i += stride) if (!order.includes(i)) order.push(i);
      }
      if (!order.includes(total - 1)) order.splice(1, 0, total - 1);
      order.forEach((i) => {
        const im = new Image();
        im.decoding = 'async';
        im.src = url(i);
        const done = (frame) => { frames[i] = frame; kick(true); };
        im.decode()
          .then(() => (ctx && window.createImageBitmap ? createImageBitmap(im) : im))
          .then(done, () => im.complete && im.naturalWidth && done(im));
      });
    };
    const nearest = (i) => {
      for (let d = 0; d < total; d++) {
        if (frames[i - d]) return i - d;
        if (frames[i + d]) return i + d;
      }
      return -1;
    };
    const show = (pos, force) => {
      const a = nearest(Math.round(pos));
      if (a < 0 || (a === current && !force)) return;
      const f = frames[a];
      if (ctx) {
        const w = f.width || f.naturalWidth, h = f.height || f.naturalHeight;
        if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(f, 0, 0, w, h);
        if (!root.classList.contains('has-cv')) root.classList.add('has-cv');
      } else {
        img.src = f.src;
      }
      current = a;
    };
    let zoomMax = 1;
    const fit = () => {
      zoomMax = 1;
      if (use3d || window.innerWidth >= 990) { root.style.removeProperty('--zoom'); return; }
      const box = frameBox.getBoundingClientRect();
      const imgH = Math.min(box.height, box.width * 822 / 960);
      zoomMax = Math.max(1, Math.min(box.height / imgH, (box.width * 0.94) / (imgH * 960 / 822 * 0.52)));
    };

    // ---------- captions on the 3D view ----------
    let shiftBase = 0;   // how far the open display slides left to make room for the caption column (px)
    let barH = 0;        // Shopify's theme preview bar, when the page is a preview: it covers the bottom of the screen
    const placeLabels = (t) => {
      if (!labels.length) return;
      // they leave while the layers close in, and come back if the visitor scrolls up
      const wide = window.innerWidth >= 990;
      const on = 1 - (wide ? smooth(0.3, 0.52, t) : smooth(0.14, 0.34, t));
      root.style.setProperty('--lab', on.toFixed(3));
      const immersive = !wide && visible && on > 0.05;
      if (immersive !== document.documentElement.classList.contains('tc-asm-immersive')) document.documentElement.classList.toggle('tc-asm-immersive', immersive);
      // the open display sits a little to the left of the captions and glides back to the centre as it closes
      root.style.setProperty('--asm-shift', (shiftBase * on).toFixed(1) + 'px');
      if (on <= 0.001) return;
      const box = labelsBox.getBoundingClientRect();
      const cvr = glCanvas.getBoundingClientRect();
      const ox = cvr.left - box.left, oy = cvr.top - box.top;
      const compW = Math.min(cvr.width, cvr.height * (0.9396 / 0.8045));   // the 960:822 framing inside the canvas
      const compR = cvr.left + (cvr.width - compW) / 2 + compW;
      // the column gets the width that is really left on the right, so a caption never crosses the edge: it wraps instead
      const room = wide ? box.right - EDGE - (compR + SIDE_GAP) : box.width * 0.42;
      const maxW = Math.round(Math.max(wide ? 150 : 96, Math.min(wide ? 210 : 120, room)));
      labels.forEach((el) => { el.style.maxWidth = maxW + 'px'; });
      const pt = (mesh, p) => { const q = gl.project(mesh, p); return { x: q.x + ox, y: q.y + oy }; };
      const items = labels.map((el, i) => {
        const [mesh, p, [ex, et, eb, ez]] = LAYERS[el.dataset.layer];
        const h = el.offsetHeight;
        return {
          el, i, line: lines[i], a: pt(mesh, p), w: el.offsetWidth, h, off: wide ? 11 : h / 2,
          e0: pt(mesh, [ex, et, ez]), e1: pt(mesh, [ex, eb, ez]), l0: pt(mesh, [-ex, et, ez]), l1: pt(mesh, [-ex, eb, ez]),
        };
      });
      const colW = Math.max(...items.map((it) => it.w));
      shiftBase = -Math.round(wide ? (SIDE_GAP + colW) * 0.42 : box.width * 0.07);
      // one column, 01 to 05 from top to bottom: 01 and 05 at the height of their own layers, 02 to 04 evenly between,
      // never closer than the minimum gap, inside the stage and clear of the chat and back-to-top buttons
      const gap = wide ? 16 : 8, pad = 6;
      const n = items.length, last = items[n - 1];
      const heights = items.reduce((sum, it) => sum + it.h, 0);
      const colRight = wide ? Math.min(compR - box.left + SIDE_GAP + colW, box.width - EDGE) : box.width - 10;
      let bottom = box.height - pad;
      const aside = !wide;   // phones: the block scrolls with the page, the chat steps aside while the captions show
      if (!aside && box.left + colRight > window.innerWidth - 150) bottom = Math.min(bottom, window.innerHeight - SAFE_BOTTOM - barH - box.top);
      const toTop = document.getElementById('to-top');
      if (!aside && toTop && toTop.classList.contains('is-visible')) {
        const r = toTop.getBoundingClientRect();
        if (r.height && r.left < box.left + colRight + 8) bottom = Math.min(bottom, r.top - 12 - box.top);
      }
      let top0 = items[0].a.y - items[0].off;
      let topN = last.a.y - last.off;
      const need = heights - last.h + gap * (n - 1);              // from the first caption's top to the last one's top
      if (topN - top0 < need) { const grow = need - (topN - top0); top0 -= grow / 2; topN += grow / 2; }
      if (topN + last.h > bottom) { const d = topN + last.h - bottom; topN -= d; top0 = Math.min(top0, topN - need); }
      if (top0 < pad) { topN += pad - top0; top0 = pad; }
      const free = (topN - top0) - (heights - last.h);             // the room left between the captions, shared equally
      let y = top0;
      items.forEach((it) => {
        it.y = y;
        y += it.h + free / (n - 1);
      });
      // each leader meets its layer's right edge exactly at the caption's height; the column is nudged when a caption
      // would fall beyond the end of its edge, so no line ever has to slant
      for (let pass = 0; pass < 3; pass++) {
        items.forEach((it) => {
          const lo = Math.min(it.e0.y, it.e1.y) + 4, hi = Math.max(it.e0.y, it.e1.y) - 4;
          const ly = it.y + it.off;
          if (hi > lo) it.y = Math.min(hi, Math.max(lo, ly)) - it.off;
        });
        for (let i = 1; i < n; i++) items[i].y = Math.max(items[i].y, items[i - 1].y + items[i - 1].h + gap);
        for (let i = n - 2; i >= 0; i--) items[i].y = Math.min(items[i].y, items[i + 1].y - items[i].h - gap);
      }
      items.forEach((it) => {
        // desktop: beside the render's framing (the canvas itself spans the whole stage); phone: against the right edge
        it.x = wide ? Math.min(compR - box.left + SIDE_GAP, box.width - EDGE - it.w) : box.width - it.w - 10;
        it.el.style.transform = `translate3d(${it.x.toFixed(1)}px, ${it.y.toFixed(1)}px, 0)`;
        const ly = it.y + it.off;
        const at = (q0, q1) => { const dy = q1.y - q0.y; const k = dy ? clamp01((ly - q0.y) / dy) : 0; return { x: q0.x + (q1.x - q0.x) * k, y: q0.y + dy * k }; };
        const r = at(it.e0, it.e1), l = at(it.l0, it.l1);
        const ex = it.x - 8;
        // phones: when the caption covers the layer's edge, the dot moves onto the layer itself, left of the caption
        let ax = r.x;
        const ay = r.y;
        if (ax > ex - 14) ax = Math.max(l.x + 8, ex - 14);
        it.line.querySelector('path').setAttribute('d', `M${ax.toFixed(1)} ${ay.toFixed(1)} L${ex.toFixed(1)} ${ly.toFixed(1)}`);
        it.line.querySelectorAll('circle').forEach((c) => { c.setAttribute('cx', ax.toFixed(1)); c.setAttribute('cy', ay.toFixed(1)); });
      });
    };

    // ---------- one render of the current state ----------
    let final = false;
    let wasDone = false;
    let flashing = false;
    const render = (force) => {
      if (gl) {
        const st = gl.draw(shown, performance.now(), idle);
        flashing = st.flashing;
        placeLabels(st.t);
      } else {
        show(shown * (total - 1), force);
      }
      steps.forEach((li, i) => li.classList.toggle('is-on', shown >= STEPS[i]));
      const done = shown >= MOTION_END;
      // the moment the five layers lock together: a gold flash runs along the acrylic edges
      if (done && !wasDone && gl && !reduce && target >= MOTION_END) { gl.flash(); kick(); }
      wasDone = done;
      root.classList.toggle('is-done', done);
      if (!gl) {
        // the HD still fades in on the last frame; a little hysteresis so it does not blink at the very end
        if (!final && current === total - 1 && shown > 0.995) final = true;
        else if (final && shown < 0.975) final = false;
        root.classList.toggle('is-final', final);
      }
      root.classList.toggle('is-moving', shown > 0.02);
      if (cta) cta.tabIndex = done ? 0 : -1;
      const p = Math.min(1, shown / MOTION_END);
      root.style.setProperty('--p', p.toFixed(3));
      if (!gl && zoomMax > 1) root.style.setProperty('--zoom', (1 + (zoomMax - 1) * p * p).toFixed(3));
    };

    // ---------- scroll position -> progress, eased with a time constant (same feel at 60 and 120 Hz) ----------
    const sticky = root.querySelector('.tc-asm__sticky');
    let pinTop = 0, pinH = 0;
    const header = document.querySelector('#header');
    const finale = root.querySelector('.tc-asm__finale');
    const opts3d = {};   // live options of the 3D scene (the phone camera angles follow the shape of the screen)
    const layout = () => {
      // the shop header stays on top of the page: start the pinned block below it
      const hdr = header ? Math.round(header.getBoundingClientRect().height) : 0;
      root.style.setProperty('--tc-hdr', hdr + 'px');
      const bar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
      barH = bar ? Math.round(Math.max(0, window.innerHeight - bar.getBoundingClientRect().top)) : 0;
      if (barH > window.innerHeight / 3) barH = 0;
      root.style.setProperty('--asm-bar', barH + 'px');
      // room taken above and below the render on desktop (heading, paddings, gap): the render gets the rest
      const cs = getComputedStyle(sticky);
      const heading = root.querySelector('.tc-asm__heading');
      // desktop: the block is as tall as its content and pins right below the header (its padding has no header
      // offset); phones: a full-screen block whose padding makes room for the header
      const wide = window.innerWidth >= 990;
      const used = parseFloat(cs.paddingTop) - (wide ? 0 : hdr) + parseFloat(cs.paddingBottom) + (heading ? heading.offsetHeight : 0) + (parseFloat(cs.rowGap) || 0) + 8;
      root.style.setProperty('--asm-head', Math.round(used) + 'px');
      if (gl && wide) {
        // desktop: the render's framing keeps room on both sides for the captions and the closing text, also in
        // narrow or very tall windows (otherwise the closing text was pushed past the right edge)
        const stageEl = root.querySelector('.tc-asm__stage');
        const side = parseFloat(getComputedStyle(stageEl).getPropertyValue('--side')) || 230;
        const maxH = Math.round(Math.max(260, stageEl.clientWidth - 2 * (side + 40)) * (0.8045 / 0.9396));
        root.style.setProperty('--asm-fh', Math.min(Math.round(stageEl.clientHeight * 0.84), maxH) + 'px');
      }
      if (!wide && finale) {
        // phones: no pinned full screen. Heading, render and closing text form one block exactly as tall as its content
        // (no empty bands above or below) that fits on one screen under the shop header. The render is as tall as the
        // closed display plus the closing text laid over its bottom (the camera keeps that room free), and the open
        // layers fan down it as far as its shape allows.
        const stageEl = root.querySelector('.tc-asm__stage');
        const avail = window.innerHeight - hdr - barH - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - (heading ? heading.offsetHeight : 0) - (parseFloat(cs.rowGap) || 0);
        const sw = stageEl.clientWidth;
        const inset = finale.offsetHeight + 18;
        // a little under the full width, so the display stays an invitation and not the whole screen
        const fw = Math.round(Math.max(sw * 0.55, Math.min(sw * 0.76, (avail - inset) / 1.625)));
        const sh = Math.round(Math.max(260, Math.min(Math.max(avail, fw * 1.625 + inset), Math.max(fw * 1.99, fw * 1.625 + inset))));
        root.style.setProperty('--asm-fw', fw + 'px');
        root.style.setProperty('--asm-sh', sh + 'px');
        // open layers: the classic diagonal on squat screens, a steeper cascade on tall ones (measured shapes:
        // [-46,-24] is 1.55 times taller than wide, [-52,-12] 1.84 times)
        const k = clamp01((sh / fw - 1.55) / (1.84 - 1.55));
        opts3d.portraitAngles = [-46 - 6 * k, -24 + 12 * k];
        if (gl) gl.setInsetBottom(inset);
      } else if (gl) gl.setInsetBottom(0);
      pinH = sticky.offsetHeight;
      pinTop = wide ? hdr : Math.max(0, (window.innerHeight - pinH) / 2);   // desktop: locks right under the header, so no gap opens above the heading
      // width of the render's 960:822 framing inside the 3D canvas: the side columns (captions, closing text) sit beside it
      if (glCanvas) { const c = glCanvas.getBoundingClientRect(); if (c.height) root.style.setProperty('--comp-w', Math.round(Math.min(c.width, c.height * (0.9396 / 0.8045))) + 'px'); }
      root.style.setProperty('--asm-h', pinH + 'px');
      root.style.setProperty('--asm-top', pinTop + 'px');
    };
    let started = false;
    const stage = root.querySelector('.tc-asm__stage');
    const measure = () => {
      const rect = root.getBoundingClientRect();
      if (!started && rect.top < window.innerHeight * 2.5 && rect.bottom > -window.innerHeight * 1.5) start();
      if (window.innerWidth < 990) {
        // phones: the block is not pinned and the layers follow the scroll: open while the block comes up the screen,
        // closed once the render reaches the middle of the screen, open again when the visitor scrolls back up
        if (reduce) { target = 1; return; }
        const st = stage.getBoundingClientRect();
        const vh = window.innerHeight;
        const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const end = (top + vh - barH) / 2 + st.height * 0.04;   // closed just before the render is centred
        const range = Math.max(240, Math.min(440, vh * 0.46));
        target = clamp01((end + range - (st.top + st.height / 2)) / range);
        return;
      }
      const range = root.offsetHeight - pinH;
      const p = range > 0 ? Math.min(1, Math.max(0, (pinTop - rect.top) / range)) : 1;
      target = reduce ? 1 : Math.min(1, p / 0.92);
    };

    let raf = 0, last = 0;
    const frame = (now) => {
      raf = 0;
      const dt = last ? Math.min(64, now - last) : 16;
      last = now;
      const diff = target - shown;
      shown = Math.abs(diff) < 0.0004 ? target : shown + diff * (1 - Math.exp(-dt / 105));
      const wantIdle = !!gl && visible && !reduce && shown >= 0.995 && !document.hidden;
      idle += ((wantIdle ? 1 : 0) - idle) * (1 - Math.exp(-dt / (wantIdle ? 900 : 180)));
      if (!wantIdle && idle < 0.003) idle = 0;
      render();
      if (shown !== target || wantIdle || idle > 0 || flashing) raf = requestAnimationFrame(frame);
      else last = 0;
    };
    function kick(force) {
      if (force && !raf) render(true);
      if (!raf) { last = 0; raf = requestAnimationFrame(frame); }
    }
    const onScroll = () => { measure(); kick(); };

    // ---------- start: WebGL first, the frames if it is not available ----------
    function start() {
      if (started) return;
      started = true;
      if (!use3d) { preload(); return; }
      const d = root.dataset;
      import(d.three)
        .then((m) => m.createAssembly(glCanvas, Object.assign(opts3d, { geo: d.geo, art: d.art, card: d.card, engrave: d.engrave, matcap: d.steel, env: d.env })))
        .then((api) => {
          gl = api;
          root.classList.add('is-3d');
          glCanvas.addEventListener('webglcontextlost', (e) => {
            // the GPU dropped the context (rare, on phones): carry on with the frames
            e.preventDefault();
            gl = null; use3d = false; root.classList.remove('is-3d', 'is-3d-ready'); loaded = false; preload();
          }, { once: true });
          gl.resize(); fit(); layout(); render(true);
          requestAnimationFrame(() => root.classList.add('is-3d-ready'));
          kick();
        })
        .catch(() => { use3d = false; preload(); kick(true); });
    }

    new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) { start(); root.classList.add('is-seen'); }
      kick();
    }).observe(root);
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) start();
    }, { rootMargin: '150% 0px' }).observe(root);

    if (finePointer) {
      // the finished display leans a little towards the mouse
      frameBox.addEventListener('pointermove', (e) => {
        if (!gl) return;
        const r = frameBox.getBoundingClientRect();
        gl.setPointer(((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1);
        kick();
      }, { passive: true });
      frameBox.addEventListener('pointerleave', () => { if (gl) { gl.setPointer(0, 0); kick(); } });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => { layout(); fit(); if (gl) gl.resize(); onScroll(); kick(true); }, { passive: true });
    document.addEventListener('visibilitychange', () => kick());
    if (window.ResizeObserver) {
      new ResizeObserver(() => { layout(); onScroll(); }).observe(sticky);
      new ResizeObserver(() => { if (gl) { gl.resize(); kick(true); } }).observe(frameBox);
    }
    layout();
    fit();
    measure();
    shown = target;
    render(true);
  };

  const scan = (scope) => scope.querySelectorAll('[data-tc-assemble]').forEach(init);
  scan(document);
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
